import { useCallback, useEffect, useState, useMemo } from "react"
import { ListGroup, ListGroupItem } from "reactstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faPlus } from "@fortawesome/free-solid-svg-icons"
import { connect } from "react-redux"
import {
  clearSelectedSearchMembers,
  searchUsers,
  setSelectedSearchMembers,
} from "../../store/actions/members/searchUsers"
import SearchInputWithLoader from "../layout/SearchInputWithLoader"
import { setCurrentPage } from "../../store/reducers/navigation"
import { Trans, useLingui } from "@lingui/react"
import { debounce } from "../../store/middlewares"

const AddExistingMembers = ({
  searchUsers,
  results,
  loading,
  error,
  clearSelectedSearchMembers,
  setPage,
  setSelectedSearchMembers,
}) => {
  const { i18n } = useLingui()
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState([])

  useEffect(() => {
    clearSelectedSearchMembers()
  }, [clearSelectedSearchMembers])

  const handleSearch = useCallback((text) => {
    setQuery(text)
    if (text.trim().length > 1) debouncedHandleSearch(text)
  }, [searchUsers])

  const debouncedHandleSearch = useMemo(() => debounce(searchUsers, 300), [searchUsers]) // 300ms debounce

  const toggleUser = (user) => {
    setSelected((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user],
    )
  }

  const isChecked = (user) => selected.some((u) => u.id === user.id)

  const handleAddUsers = useCallback(() => {
    setSelectedSearchMembers(selected)
    setPage("group-assignMemberRoles")
  }, [selected, setSelectedSearchMembers, setPage])

  return (
    <div style={styles.container}>
      <h5 style={styles.title}>
        <Trans id="add_existing_members" />
      </h5>
      <SearchInputWithLoader
        value={query}
        onChange={handleSearch}
        loading={loading}
        placeholder={i18n._("Search by name")}
      />
      {!loading && query.trim().length === 0 && (
        <p style={styles.infoMessage}>
          <Trans id="start_typing_name" />
        </p>
      )}
      {!loading && query.length > 1 && (
        <>
          {error ? (
            <p style={styles.errorMessage}>
              ⚠️ {error || <Trans id="fetch_error" />}
            </p>
          ) : results.length === 0 ? (
            <p style={styles.noResults}>
              <Trans id="no_results" /> "<strong>{query}</strong>"
            </p>
          ) : (
            <>
              <p style={styles.subtext}>
                <Trans id="select_users" />
              </p>
              <ListGroup style={{ borderRadius: 8, overflow: "hidden" }}>
                {results.map((user) => {
                  const initials =
                    user.full_name
                      ?.split(" ")
                      .filter(Boolean)
                      .slice(0, 2) // take first 2 words only (e.g., "Jean Love" -- "JL")
                      .map((n) => n[0].toUpperCase())
                      .join("") || "?"
                  return (
                    <ListGroupItem
                      key={user.id}
                      onClick={() => toggleUser(user)}
                      style={{
                        ...styles.item,
                        backgroundColor: isChecked(user) ? "#FFF3E0" : "#fff",
                        boxShadow: "inset 0 -1px 0 #f5f5f5",
                      }}
                    >
                      <div style={styles.avatar}>
                        {initials.trim() ? (
                          initials
                        ) : (
                          <FontAwesomeIcon icon={faUser} />
                        )}
                      </div>
                      <div style={styles.name}>{user.full_name}</div>
                      <input type="checkbox" checked={isChecked(user)} readOnly />
                    </ListGroupItem>
                  )
                })}
              </ListGroup>

              <div style={styles.fabWrapper}>
                <button
                  onClick={handleAddUsers}
                  disabled={selected.length === 0}
                  style={{
                    ...styles.fabButton,
                    opacity: selected.length === 0 ? 0.5 : 1,
                    cursor: selected.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
                  <Trans id="add_users" />
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

const styles = {
  container: {
    padding: "20px",
    fontFamily: "Fira Sans",
    backgroundColor: "#fff",
    minHeight: "100vh",
  },
  title: {
    color: "#e36f1e",
    fontWeight: 700,
    fontSize: "18px",
    marginBottom: "18px",
  },
  subtext: {
    fontSize: "14px",
    color: "#444",
    marginBottom: "10px",
    marginTop: "8px",
  },
  item: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  avatar: {
    backgroundColor: "#FFD600",
    borderRadius: "50%",
    width: "38px",
    height: "38px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "14px",
    marginRight: "12px",
    color: "#000",
    flexShrink: 0,
  },
  name: {
    flexGrow: 1,
    fontSize: "15px",
    color: "#333",
  },
  fabWrapper: {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
  },
  fabButton: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: "#e36f1e",
    color: "#fff",
    border: "none",
    borderRadius: "32px",
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
    zIndex: 1000,
    transition: "background-color 0.3s ease",
    cursor: "pointer",
  },
  noResults: {
    fontSize: "14px",
    color: "#999",
    textAlign: "center",
    marginTop: "24px",
  },
  errorMessage: {
    fontSize: "14px",
    color: "#d32f2f",
    backgroundColor: "#ffebee",
    padding: "12px",
    borderRadius: "6px",
    textAlign: "center",
    marginTop: "20px",
    border: "1px solid #f44336",
  },
  infoMessage: {
    fontSize: "14px",
    color: "#666",
    textAlign: "center",
    marginTop: "20px",
    fontStyle: "italic",
  },
}

const mapStateToProps = (state) => ({
  results: state.searchUsers.results,
  loading: state.searchUsers.loading,
  error: state.searchUsers.error,
})

const mapDispatchToProps = (dispatch) => ({
  searchUsers: (query) => dispatch(searchUsers(query)),
  setPage: (page) => dispatch(setCurrentPage(page)),
  setSelectedSearchMembers: (members) => dispatch(setSelectedSearchMembers(members)),
  clearSelectedSearchMembers: () => dispatch(clearSelectedSearchMembers()),
})

export default connect(mapStateToProps, mapDispatchToProps)(AddExistingMembers)
